import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import DashboardLayout from '../components/DashboardLayout';
import countriesData from '../data/countries.json';
import {
  Building2,
  ChevronDown,
  ChevronLeft,
  RefreshCw,
  Info,
  Server,
  CheckCircle,
  XCircle,
  Shield,
  Fingerprint,
  Link2,
  Power,
  Copy,
  Trash2,
  X,
} from 'lucide-react';
import './Dashboard.css';
import './TenantConfigurations.css';
import { parseScopeFromApi, scopeArrayToApiString } from '../lib/oidcScopeForm';
import OidcScopeSelect from '../components/OidcScopeSelect';

const COUNTRIES_SORTED = [...countriesData].sort((a, b) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
);

/** Treat API/Dynamo enabled flags consistently (avoids string "false" counting as truthy). */
function isServiceEnabled(row) {
  const v = row?.enabled;
  if (v === false || v === 'false' || v === 0 || v === '0') return false;
  if (v === true || v === 'true' || v === 1) return true;
  return Boolean(v);
}

const DEFAULT_OIDC_FORM = {
  clientName: '',
  redirectUris: [''],
  responseTypes: ['code'],
  grantTypes: ['authorization_code', 'refresh_token'],
  tokenEndpointAuthMethod: 'client_secret_basic',
  scope: ['openid', 'profile', 'email'],
};

const OIDC_RESPONSE_TYPES = ['code', 'id_token', 'token'];
const OIDC_GRANT_TYPES = ['authorization_code', 'refresh_token', 'client_credentials'];

function CountryPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return 'Choose countries…';
    const parts = value.slice(0, 2).map((code) => {
      const row = COUNTRIES_SORTED.find((c) => c.code === code);
      return row ? `${row.name} (${code})` : code;
    });
    if (value.length > 2) {
      parts.push(`+${value.length - 2} more`);
    }
    return parts.join(', ');
  }, [value]);

  /** Filtered list; always include currently selected codes so they stay visible and deselectable. */
  const options = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const base = !qq
      ? COUNTRIES_SORTED
      : COUNTRIES_SORTED.filter(
          (c) =>
            c.name.toLowerCase().includes(qq) || c.code.toLowerCase().includes(qq)
        );
    const shown = new Set(base.map((c) => c.code));
    const missingSelected = value.filter((code) => !shown.has(code));
    if (missingSelected.length === 0) return base;
    const extraRows = missingSelected
      .map((code) => COUNTRIES_SORTED.find((c) => c.code === code))
      .filter(Boolean);
    return [...extraRows, ...base];
  }, [q, value]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const toggleCode = (code, checked) => {
    if (disabled) return;
    if (checked) {
      if (!selectedSet.has(code)) {
        onChange([...value, code].sort((a, b) => a.localeCompare(b)));
      }
    } else {
      onChange(value.filter((c) => c !== code));
    }
  };

  return (
    <div className="country-picker" ref={wrapRef}>
      <p className="form-hint" style={{ marginBottom: '0.5rem' }}>
        Restrict adaptive auth to these countries only. Leave none selected for no restriction.
      </p>
      <button
        type="button"
        className="country-picker-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={`country-picker-trigger-text ${value.length === 0 ? 'country-picker-placeholder' : ''}`}>
          {triggerLabel}
        </span>
        <ChevronDown className={`country-picker-chevron ${open ? 'country-picker-chevron-open' : ''}`} aria-hidden />
      </button>
      {open && (
        <div className="country-picker-panel" role="presentation">
          <input
            ref={searchRef}
            type="search"
            className="country-picker-search"
            placeholder="Search by name or ISO code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={disabled}
            autoComplete="off"
            aria-label="Filter countries"
            onKeyDown={(e) => e.stopPropagation()}
          />
          <div className="country-picker-list" role="group" aria-label="Country options">
            {options.length === 0 ? (
              <p className="country-picker-empty">No matches — clear search</p>
            ) : (
              options.map((c) => (
                <label key={c.code} className="country-picker-row">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(c.code)}
                    onChange={(e) => toggleCode(c.code, e.target.checked)}
                    disabled={disabled}
                  />
                  <span className="country-picker-row-text">
                    {c.name}{' '}
                    <span className="country-picker-code">({c.code})</span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
      {!open && value.length > 0 && (
        <p className="country-picker-selected-summary">
          <strong>{value.length}</strong> selected: {value.join(', ')}
        </p>
      )}
    </div>
  );
}

function TenantConfigurations() {
  const { token, loading, user, refreshSharedCredentials } = useContext(AuthContext);
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const { showSuccess, showError } = useFlashMessage();

  const [adminTenants, setAdminTenants] = useState([]);
  const [loadingAdminList, setLoadingAdminList] = useState(true);
  const [listLoadError, setListLoadError] = useState(null);

  const [tab, setTab] = useState('general');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  /** Sub-tabs inside Services: enabled vs disabled list */
  const [servicesListTab, setServicesListTab] = useState('enabled');
  const [oidcClient, setOidcClient] = useState(null);
  const [oidcForm, setOidcForm] = useState(() => ({ ...DEFAULT_OIDC_FORM }));

  const [form, setForm] = useState({
    displayName: '',
    status: 'active',
    redirection_url: '',
    trustWindowDays: 30,
    authRequestTimeWindowMinutes: 1,
    allowedCountries: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchAdminTenants = useCallback(async () => {
    if (!token) return [];
    const res = await axios.get(`${API_CONFIG.BASE_URL}/user/shared-credentials`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const list = Array.isArray(res.data) ? res.data : res.data?.sharedCredentials || [];
    return list.filter((c) => c.is_admin === true);
  }, [token]);

  const loadDetail = useCallback(async () => {
    if (!token || !tenantId) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    setLoadingDetail(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}`, authHeaders),
        axios.get(`${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/services`, authHeaders),
      ]);
      const t = tRes.data?.tenant;
      setTenant(t);
      if (t) {
        const countries = t.allowedCountries || [];
        setForm({
          displayName: t.displayName || t.name || '',
          status: t.status || 'active',
          redirection_url: t.redirection_url || '',
          trustWindowDays: t.trustWindowDays ?? 30,
          authRequestTimeWindowMinutes: t.authRequestTimeWindowMinutes ?? 1,
          allowedCountries: Array.isArray(countries) ? [...countries] : [],
        });
      }
      setServices(sRes.data?.data || []);

      try {
        const oRes = await axios.get(
          `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/oidc-client`,
          authHeaders
        );
        const oc = oRes.data;
        setOidcClient(oc);
        setOidcForm({
          clientName: oc.clientName || '',
          redirectUris:
            Array.isArray(oc.redirectUris) && oc.redirectUris.length > 0
              ? [...oc.redirectUris]
              : [''],
          responseTypes:
            Array.isArray(oc.responseTypes) && oc.responseTypes.length
              ? [...oc.responseTypes]
              : ['code'],
          grantTypes:
            Array.isArray(oc.grantTypes) && oc.grantTypes.length
              ? [...oc.grantTypes]
              : ['authorization_code', 'refresh_token'],
          tokenEndpointAuthMethod: oc.tokenEndpointAuthMethod || 'client_secret_basic',
          scope: parseScopeFromApi(oc.scope),
        });
      } catch (e) {
        if (e.response?.status === 404) {
          setOidcClient(null);
          setOidcForm({ ...DEFAULT_OIDC_FORM });
        } else {
          throw e;
        }
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || err.response?.data?.error || 'Failed to load tenant');
    } finally {
      setLoadingDetail(false);
    }
  }, [token, tenantId, showError]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  const loadAdminTenantList = useCallback(async () => {
    if (!token) {
      setLoadingAdminList(false);
      return;
    }
    setLoadingAdminList(true);
    setListLoadError(null);
    try {
      const admins = await fetchAdminTenants();
      setAdminTenants(admins);
      refreshSharedCredentials();
    } catch (e) {
      console.error(e);
      setAdminTenants([]);
      setListLoadError(
        'We could not load your tenant list. Check your connection and try again.'
      );
      showError('Could not load tenant admin access.');
    } finally {
      setLoadingAdminList(false);
    }
  }, [token, fetchAdminTenants, refreshSharedCredentials, showError]);

  useEffect(() => {
    if (tenantId) return;
    loadAdminTenantList();
  }, [tenantId, loadAdminTenantList]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      setLoadingAdminList(true);
      try {
        const admins = await fetchAdminTenants();
        if (cancelled) return;
        const allowed = admins.some((c) => c.tenantId === tenantId);
        if (!allowed) {
          showError('You do not have tenant admin access for this tenant.');
          navigate('/dashboard/tenant-configurations');
          return;
        }
        await loadDetail();
        refreshSharedCredentials();
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          showError('Could not verify access.');
          navigate('/dashboard/tenant-configurations');
        }
      } finally {
        if (!cancelled) setLoadingAdminList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, fetchAdminTenants, loadDetail, navigate, refreshSharedCredentials, showError]);

  const saveGeneral = async (e) => {
    e.preventDefault();
    if (!form.displayName.trim()) {
      showError('Please enter a display name.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}`,
        {
          displayName: form.displayName.trim(),
          status: form.status,
          redirection_url: form.redirection_url.trim(),
          trustWindowDays: Math.min(365, Math.max(1, Number(form.trustWindowDays) || 30)),
          authRequestTimeWindowMinutes: Math.min(
            60,
            Math.max(1, Number(form.authRequestTimeWindowMinutes) || 1)
          ),
          allowedCountries: form.allowedCountries,
        },
        authHeaders
      );
      showSuccess('Tenant settings saved');
      loadDetail();
      refreshSharedCredentials();
    } catch (err) {
      showError(err.response?.data?.error || err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleService = async (row) => {
    const on = isServiceEnabled(row);
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/services/${row.serviceId}`,
        {
          enabled: !on,
          maxApiCount: row.maxApiCount ?? 100000,
          enableBiometricBasedApproval: !!row.enableBiometricBasedApproval,
        },
        authHeaders
      );
      showSuccess(!on ? 'Service enabled' : 'Service disabled');
      loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    }
  };

  const toggleBiometric = async (row) => {
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/services/${row.serviceId}`,
        {
          enabled: isServiceEnabled(row),
          maxApiCount: row.maxApiCount ?? 100000,
          enableBiometricBasedApproval: !row.enableBiometricBasedApproval,
        },
        authHeaders
      );
      showSuccess(
        !row.enableBiometricBasedApproval
          ? 'Biometric approval enabled'
          : 'Biometric approval disabled'
      );
      loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    }
  };

  const enableAllServices = async () => {
    if (!window.confirm('Enable all services for this tenant with 100,000 max API count per month?')) return;
    try {
      await axios.post(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/services/enable-all`,
        { maxApiCount: 100000 },
        authHeaders
      );
      showSuccess('All services enabled');
      loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'Request failed');
    }
  };

  const oidcPayloadFromForm = useCallback(() => {
    const raw = Array.isArray(oidcForm.redirectUris) ? oidcForm.redirectUris : [];
    const redirectUris = raw
      .map((u) => (typeof u === 'string' ? u.trim() : ''))
      .filter((u) => u !== '');
    return {
      clientName: (oidcForm.clientName || '').trim(),
      redirectUris,
      responseTypes: oidcForm.responseTypes?.length ? oidcForm.responseTypes : ['code'],
      grantTypes:
        oidcForm.grantTypes?.length
          ? oidcForm.grantTypes
          : ['authorization_code', 'refresh_token'],
      tokenEndpointAuthMethod: oidcForm.tokenEndpointAuthMethod || 'client_secret_basic',
      scope: scopeArrayToApiString(oidcForm.scope),
    };
  }, [oidcForm]);

  const updateOidcRedirectUri = useCallback((index, value) => {
    setOidcForm((prev) => {
      const uris = [...(prev.redirectUris || [])];
      uris[index] = value;
      return { ...prev, redirectUris: uris };
    });
  }, []);

  const addOidcRedirectUri = useCallback(() => {
    setOidcForm((prev) => ({
      ...prev,
      redirectUris: [...(prev.redirectUris || []), ''],
    }));
  }, []);

  const removeOidcRedirectUri = useCallback((index) => {
    setOidcForm((prev) => {
      const uris = [...(prev.redirectUris || [])];
      return { ...prev, redirectUris: uris.filter((_, i) => i !== index) };
    });
  }, []);

  const saveOidc = async (e) => {
    e?.preventDefault?.();
    const body = oidcPayloadFromForm();
    if (!body.redirectUris.length) {
      showError('Please add at least one redirect URI.');
      return;
    }
    setSaving(true);
    try {
      if (oidcClient) {
        await axios.put(
          `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/oidc-client`,
          body,
          authHeaders
        );
      } else {
        await axios.post(
          `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/oidc-client`,
          { ...body, tenantId },
          authHeaders
        );
      }
      showSuccess('OIDC client saved');
      loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'OIDC save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteOidc = async () => {
    if (!oidcClient) return;
    if (
      !window.confirm(
        'Delete this OIDC client? Applications using it will fail until you create a new client.'
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await axios.delete(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/oidc-client`,
        authHeaders
      );
      showSuccess('OIDC client deleted');
      await loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const copyOidcClientId = () => {
    if (!oidcClient?.clientId) return;
    navigator.clipboard.writeText(oidcClient.clientId).then(
      () => showSuccess('Client ID copied'),
      () => showError('Could not copy')
    );
  };

  const copyCredentialValue = (value, shortLabel) => {
    const s = value != null && value !== '' ? String(value) : '';
    if (!s) {
      showError('Nothing to copy');
      return;
    }
    navigator.clipboard.writeText(s).then(
      () => showSuccess(`${shortLabel} copied`),
      () => showError('Could not copy')
    );
  };

  const regenerateHmac = async () => {
    const typed = window.prompt('Type ROTATE to confirm HMAC key rotation.');
    if (typed !== 'ROTATE') return;
    try {
      await axios.post(
        `${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}/regenerate-hmac`,
        {},
        authHeaders
      );
      showSuccess('HMAC key rotated');
      loadDetail();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to rotate HMAC');
    }
  };

  const deleteTenant = async () => {
    const typed = window.prompt('Type the tenant ID to permanently delete this tenant.');
    if (typed !== tenantId) return;
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/user/tenant-admin/${tenantId}`, authHeaders);
      showSuccess('Tenant deleted');
      refreshSharedCredentials();
      navigate('/dashboard/tenant-configurations');
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed');
    }
  };

  const enabledServices = useMemo(() => services.filter((s) => isServiceEnabled(s)), [services]);
  const disabledServices = useMemo(() => services.filter((s) => !isServiceEnabled(s)), [services]);

  useEffect(() => {
    if (
      servicesListTab === 'enabled' &&
      enabledServices.length === 0 &&
      disabledServices.length > 0
    ) {
      setServicesListTab('disabled');
    } else if (
      servicesListTab === 'disabled' &&
      disabledServices.length === 0 &&
      enabledServices.length > 0
    ) {
      setServicesListTab('enabled');
    }
  }, [servicesListTab, enabledServices.length, disabledServices.length]);

  /** Same as syberkey-admin TenantDetailPage: bulk "Enable all" only when there are zero mappings. */
  const showEnableAllCta = services.length === 0;
  const allServicesEnabled =
    services.length > 0 && disabledServices.length === 0;

  const getUsagePercentage = (current, max) => {
    const m = Number(max) || 0;
    const c = Number(current) || 0;
    if (m <= 0) return 0;
    return Math.min((c / m) * 100, 100);
  };

  if (!user) return null;

  if (!tenantId) {
    return (
      <DashboardLayout activeNav="tenant-configurations">
        <div className="dashboard-section tenant-config-page">
          <h2>
            <Building2 size={24} /> Tenants / configurations
          </h2>
          <p className="list-intro">
            Choose a tenant where you have <strong>tenant admin</strong> access to edit settings,
            services, and OIDC — same capabilities as the admin portal for that tenant.
          </p>
          {loadingAdminList ? (
            <div className="tenant-config-loading">
              <RefreshCw className="animate-spin" size={20} />
              <span>Loading your tenant admin access…</span>
            </div>
          ) : listLoadError ? (
            <div className="tenant-config-empty tenant-config-empty--error" role="alert">
              <p>{listLoadError}</p>
              <button type="button" className="btn-primary" onClick={() => loadAdminTenantList()}>
                Try again
              </button>
            </div>
          ) : adminTenants.length === 0 ? (
            <div className="tenant-config-empty tenant-config-empty--none">
              <Info size={36} className="tenant-config-empty-icon" aria-hidden />
              <h3 className="tenant-config-empty-title">No tenants to manage</h3>
              <p className="tenant-config-empty-text">
                You are not marked as <strong>tenant admin</strong> on any organization yet. When a
                global administrator enables that role on your account for a tenant, it will appear
                here.
              </p>
              <p className="tenant-config-empty-hint">
                You must <strong>share your credentials</strong> with a tenant before you can be
                granted tenant admin on that mapping.
              </p>
            </div>
          ) : (
            <ul className="tenant-admin-list">
              {adminTenants.map((c) => (
                <li key={c.tenantId}>
                  <button
                    type="button"
                    className="tenant-admin-card-btn"
                    onClick={() => navigate(`/dashboard/tenant-configurations/${c.tenantId}`)}
                  >
                    <strong>{c.tenantName || c.tenantId}</strong>
                    <span className="tenant-id">{c.tenantId}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'services', label: 'Services' },
    { id: 'oidc', label: 'OIDC' },
    { id: 'danger', label: 'Danger zone', danger: true },
  ];

  return (
    <DashboardLayout activeNav="tenant-configurations">
      <div className="dashboard-section tenant-config-page">
        <div className="tenant-config-header">
          <button
            type="button"
            className="tenant-config-back"
            onClick={() => navigate('/dashboard/tenant-configurations')}
          >
            <ChevronLeft size={18} /> Back to tenants
          </button>
          <div className="tenant-config-title-row">
            <h2>
              <Building2 size={24} /> {tenant?.displayName || tenant?.name || tenantId}
            </h2>
          </div>
          <p className="tenant-config-meta">
            Tenant ID: <code>{tenantId}</code>
          </p>
        </div>

        <div className="tenant-config-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tab-btn ${tab === t.id ? 'active' : ''} ${t.danger ? 'tab-danger' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loadingAdminList || loadingDetail ? (
          <div className="tenant-config-loading">
            <RefreshCw className="animate-spin" size={22} />
            <span>Loading tenant…</span>
          </div>
        ) : (
          <>
            {tab === 'general' && (
              <>
              <div className="tenant-config-card tenant-config-card--credentials">
                <h3>Tenant credentials</h3>
                <p className="tenant-credentials-hint">
                  API key and HMAC are sensitive — use only in trusted server-side integrations. Copy each
                  field as needed.
                </p>
                <div className="tenant-credential-row">
                  <label className="tenant-credential-label" htmlFor="tc-cred-tenant-id">
                    Tenant ID
                  </label>
                  <div className="tenant-credential-value-wrap">
                    <input
                      id="tc-cred-tenant-id"
                      className="tenant-credential-input"
                      readOnly
                      value={tenant?.tenantId || tenant?.id || tenantId || ''}
                      placeholder="—"
                    />
                    <button
                      type="button"
                      className="tenant-credential-copy-btn"
                      onClick={() =>
                        copyCredentialValue(
                          tenant?.tenantId || tenant?.id || tenantId,
                          'Tenant ID'
                        )
                      }
                      title="Copy tenant ID"
                      aria-label="Copy tenant ID"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
                <div className="tenant-credential-row">
                  <label className="tenant-credential-label" htmlFor="tc-cred-api-key">
                    API key
                  </label>
                  <div className="tenant-credential-value-wrap">
                    <input
                      id="tc-cred-api-key"
                      className="tenant-credential-input"
                      readOnly
                      value={tenant?.apiKey || ''}
                      placeholder="—"
                    />
                    <button
                      type="button"
                      className="tenant-credential-copy-btn"
                      onClick={() => copyCredentialValue(tenant?.apiKey, 'API key')}
                      title="Copy API key"
                      aria-label="Copy API key"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
                <div className="tenant-credential-row">
                  <label className="tenant-credential-label" htmlFor="tc-cred-hmac">
                    HMAC key
                  </label>
                  <div className="tenant-credential-value-wrap">
                    <input
                      id="tc-cred-hmac"
                      className="tenant-credential-input"
                      readOnly
                      value={tenant?.hmacKey || ''}
                      placeholder="—"
                    />
                    <button
                      type="button"
                      className="tenant-credential-copy-btn"
                      onClick={() => copyCredentialValue(tenant?.hmacKey, 'HMAC key')}
                      title="Copy HMAC key"
                      aria-label="Copy HMAC key"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="tenant-config-card">
                <h3>Edit tenant</h3>
                <form onSubmit={saveGeneral} className="tenant-config-form">
                  <div className="form-group">
                    <label className="form-label" htmlFor="tc-displayName">
                      Display name *
                    </label>
                    <input
                      id="tc-displayName"
                      className="form-input"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="tc-status">
                      Status *
                    </label>
                    <select
                      id="tc-status"
                      className="form-input"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      disabled={saving}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div className="form-group form-group--full">
                    <label className="form-label" htmlFor="tc-redir">
                      Redirection URL (optional)
                    </label>
                    <input
                      id="tc-redir"
                      className="form-input"
                      type="url"
                      value={form.redirection_url}
                      onChange={(e) => setForm({ ...form, redirection_url: e.target.value })}
                      disabled={saving}
                      placeholder="https://example.com/redirect"
                    />
                    <p className="form-hint">Where to send users after authentication, when configured.</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="tc-trust">
                      Trust window (days)
                    </label>
                    <input
                      id="tc-trust"
                      className="form-input"
                      type="number"
                      min={1}
                      max={365}
                      value={form.trustWindowDays}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          trustWindowDays: Math.min(365, Math.max(1, Number(e.target.value) || 30)),
                        })
                      }
                      disabled={saving}
                    />
                    <p className="form-hint">
                      How long to trust a device after MFA without re-challenge (adaptive MFA). Default:
                      30 days.
                    </p>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="tc-authwin">
                      Auth request time window (minutes)
                    </label>
                    <input
                      id="tc-authwin"
                      className="form-input"
                      type="number"
                      min={1}
                      max={60}
                      value={form.authRequestTimeWindowMinutes}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          authRequestTimeWindowMinutes: Math.min(
                            60,
                            Math.max(1, Number(e.target.value) || 1)
                          ),
                        })
                      }
                      disabled={saving}
                    />
                    <p className="form-hint">
                      How long an MFA approval request stays active before expiring. Default: 1 minute.
                    </p>
                  </div>
                  <div className="form-group form-group--full">
                    <span className="form-label">Allowed countries</span>
                    <CountryPicker
                      value={form.allowedCountries}
                      onChange={(codes) => setForm({ ...form, allowedCountries: codes })}
                      disabled={saving}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-save" disabled={saving}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </div>
              </>
            )}

            {tab === 'services' && (
              <div className="tenant-config-card tenant-config-card--services">
                <div className="service-mgmt-header">
                  <h3>
                    <Server size={20} /> Service management
                  </h3>
                  <button
                    type="button"
                    className="btn-services-refresh"
                    onClick={() => loadDetail()}
                    disabled={loadingDetail}
                    title="Reload service list"
                  >
                    <RefreshCw size={16} className={loadingDetail ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                </div>

                {services.length > 0 && (
                  <div className="services-toolbar">
                    <p className="form-hint" style={{ margin: 0, flex: 1 }}>
                      Enable or disable SyberKey services for this tenant. Use biometric approval where
                      supported.
                    </p>
                  </div>
                )}

                {allServicesEnabled && (
                  <div className="services-all-enabled" role="status">
                    <CheckCircle size={18} aria-hidden />
                    <span>All services are enabled for this tenant.</span>
                  </div>
                )}

                {services.length > 0 && (
                  <div className="service-list-tabs">
                    <div
                      className="service-list-tabs-bar"
                      role="tablist"
                      aria-label="Enabled and disabled services"
                    >
                      <button
                        type="button"
                        role="tab"
                        id="tab-services-enabled"
                        aria-controls="panel-services-enabled"
                        aria-selected={servicesListTab === 'enabled'}
                        className={`service-list-tab ${servicesListTab === 'enabled' ? 'active' : ''}`}
                        onClick={() => setServicesListTab('enabled')}
                      >
                        <CheckCircle size={16} className="service-section-icon service-section-icon--on" aria-hidden />
                        Enabled ({enabledServices.length})
                      </button>
                      <button
                        type="button"
                        role="tab"
                        id="tab-services-disabled"
                        aria-controls="panel-services-disabled"
                        aria-selected={servicesListTab === 'disabled'}
                        className={`service-list-tab ${servicesListTab === 'disabled' ? 'active' : ''}`}
                        onClick={() => setServicesListTab('disabled')}
                      >
                        <XCircle size={16} className="service-section-icon service-section-icon--off" aria-hidden />
                        Disabled ({disabledServices.length})
                      </button>
                    </div>

                    {servicesListTab === 'enabled' && (
                      <div
                        id="panel-services-enabled"
                        role="tabpanel"
                        aria-labelledby="tab-services-enabled"
                        className="service-list-tab-panel"
                      >
                        {enabledServices.length > 0 ? (
                          <div className="service-grid">
                            {enabledServices.map((row) => {
                              const pct = getUsagePercentage(row.currentApiCount, row.maxApiCount);
                              const barTone =
                                pct > 90
                                  ? 'service-api-fill--high'
                                  : pct > 70
                                    ? 'service-api-fill--mid'
                                    : 'service-api-fill--low';
                              return (
                                <div key={row.mappingId || row.serviceId} className="service-card service-card--on">
                                  <div className="service-card-head">
                                    <div>
                                      <h4>{row.service?.name || row.serviceId}</h4>
                                      <div className="service-card-id">{row.serviceId}</div>
                                      {row.service?.description && (
                                        <p className="service-card-desc">{row.service.description}</p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className="service-power-btn service-power-btn--disable"
                                      onClick={() => toggleService(row)}
                                      title="Disable service"
                                    >
                                      <Power size={20} strokeWidth={2} />
                                    </button>
                                  </div>
                                  <div className="service-bio-row">
                                    <span className="service-bio-label">
                                      <Fingerprint size={16} />
                                      Biometric approval
                                    </span>
                                    <button
                                      type="button"
                                      className={`service-bio-pill ${row.enableBiometricBasedApproval ? 'on' : ''}`}
                                      onClick={() => toggleBiometric(row)}
                                    >
                                      {row.enableBiometricBasedApproval ? 'On' : 'Off'}
                                    </button>
                                  </div>
                                  <div className="service-api-block">
                                    <div className="service-api-labels">
                                      <span>API usage</span>
                                      <span>
                                        {(row.currentApiCount ?? 0).toLocaleString()} /{' '}
                                        {(row.maxApiCount ?? 0).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="service-api-track">
                                      <div
                                        className={`service-api-fill ${barTone}`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <p className="service-api-reset">
                                      Resets:{' '}
                                      {row.resetDate ? new Date(row.resetDate).toLocaleDateString() : '—'}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="service-list-tab-empty">No enabled services.</p>
                        )}
                      </div>
                    )}

                    {servicesListTab === 'disabled' && (
                      <div
                        id="panel-services-disabled"
                        role="tabpanel"
                        aria-labelledby="tab-services-disabled"
                        className="service-list-tab-panel"
                      >
                        {disabledServices.length > 0 ? (
                          <div className="service-grid">
                            {disabledServices.map((row) => (
                              <div key={row.mappingId || row.serviceId} className="service-card service-card--off">
                                <div className="service-card-head">
                                  <div>
                                    <h4>{row.service?.name || row.serviceId}</h4>
                                    <div className="service-card-id">{row.serviceId}</div>
                                    {row.service?.description && (
                                      <p className="service-card-desc">{row.service.description}</p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="service-power-btn service-power-btn--enable"
                                    onClick={() => toggleService(row)}
                                    title="Enable service"
                                  >
                                    <Power size={20} strokeWidth={2} />
                                  </button>
                                </div>
                                <div className="service-bio-row">
                                  <span className="service-bio-label">
                                    <Fingerprint size={16} />
                                    Biometric approval
                                  </span>
                                  <button
                                    type="button"
                                    className={`service-bio-pill ${row.enableBiometricBasedApproval ? 'on' : ''}`}
                                    onClick={() => toggleBiometric(row)}
                                  >
                                    {row.enableBiometricBasedApproval ? 'On' : 'Off'}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="service-list-tab-empty">No disabled services.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {showEnableAllCta && !loadingDetail && (
                  <div className="services-empty-state">
                    <Server size={40} className="services-empty-icon" aria-hidden />
                    <p>No services configured for this tenant.</p>
                    <button type="button" className="btn-enable-all services-empty-cta" onClick={enableAllServices}>
                      Enable all services
                    </button>
                    <p className="form-hint services-empty-hint">
                      Provisions every SyberKey service with 100,000 max API calls per month (same as admin).
                    </p>
                  </div>
                )}
              </div>
            )}

            {tab === 'oidc' && (
              <div className="tenant-config-card">
                <div className="oidc-tab-header">
                  <h3>
                    <Link2 size={20} /> OIDC client
                  </h3>
                  <button
                    type="button"
                    className="btn-services-refresh"
                    onClick={() => loadDetail()}
                    disabled={loadingDetail || saving}
                    title="Reload OIDC client"
                  >
                    <RefreshCw size={16} className={loadingDetail ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                </div>
                <p className="oidc-intro">
                  Same fields as the admin portal OIDC client editor: register redirect URIs, scopes, and
                  grant types for apps that sign in with this tenant.
                </p>
                <form onSubmit={saveOidc} className="tenant-config-form oidc-form">
                  {oidcClient?.clientId && (
                    <div className="form-group form-group--full">
                      <span className="form-label">Client ID</span>
                      <div className="oidc-client-id-row">
                        <code className="oidc-client-id">{oidcClient.clientId}</code>
                        <button
                          type="button"
                          className="oidc-copy-btn"
                          onClick={copyOidcClientId}
                          title="Copy client ID"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label" htmlFor="oidc-clientName">
                      Client name
                    </label>
                    <input
                      id="oidc-clientName"
                      className="form-input"
                      value={oidcForm.clientName}
                      onChange={(e) => setOidcForm({
                        ...oidcForm,
                        clientName: e.target.value,
                      })}
                      disabled={saving}
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="oidc-token">
                      Token endpoint auth method
                    </label>
                    <select
                      id="oidc-token"
                      className="form-input"
                      value={oidcForm.tokenEndpointAuthMethod}
                      onChange={(e) =>
                        setOidcForm({
                          ...oidcForm,
                          tokenEndpointAuthMethod: e.target.value,
                        })
                      }
                      disabled={saving}
                    >
                      <option value="client_secret_basic">Client secret basic</option>
                      <option value="client_secret_post">Client secret post</option>
                      <option value="none">None (public client)</option>
                    </select>
                  </div>
                  <div className="form-group form-group--full">
                    <label className="form-label" htmlFor="oidc-redirect-0">
                      Redirect URIs *
                    </label>
                    <div className="oidc-redirect-rows">
                      {(oidcForm.redirectUris?.length ? oidcForm.redirectUris : ['']).map((uri, index) => (
                        <div key={index} className="oidc-redirect-row">
                          <input
                            id={index === 0 ? 'oidc-redirect-0' : undefined}
                            className="form-input"
                            type="url"
                            value={uri}
                            onChange={(e) => updateOidcRedirectUri(index, e.target.value)}
                            disabled={saving}
                            placeholder="https://example.com/callback"
                            required={index === 0}
                            autoComplete="off"
                          />
                          {oidcForm.redirectUris && oidcForm.redirectUris.length > 1 && (
                            <button
                              type="button"
                              className="btn-oidc-remove-uri"
                              onClick={() => removeOidcRedirectUri(index)}
                              disabled={saving}
                              title="Remove redirect URI"
                            >
                              <X size={18} aria-hidden />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn-oidc-add-uri"
                      onClick={addOidcRedirectUri}
                      disabled={saving}
                    >
                      + Add redirect URI
                    </button>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Response types</span>
                    <div className="oidc-checkbox-row">
                      {OIDC_RESPONSE_TYPES.map((type) => (
                        <label key={type} className="oidc-check">
                          <input
                            type="checkbox"
                            checked={oidcForm.responseTypes.includes(type)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...oidcForm.responseTypes, type]
                                : oidcForm.responseTypes.filter((t) => t !== type);
                              setOidcForm({ ...oidcForm, responseTypes: next });
                            }}
                            disabled={saving}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Grant types</span>
                    <div className="oidc-checkbox-row">
                      {OIDC_GRANT_TYPES.map((type) => (
                        <label key={type} className="oidc-check">
                          <input
                            type="checkbox"
                            checked={oidcForm.grantTypes.includes(type)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...oidcForm.grantTypes, type]
                                : oidcForm.grantTypes.filter((t) => t !== type);
                              setOidcForm({ ...oidcForm, grantTypes: next });
                            }}
                            disabled={saving}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group form-group--full">
                    <label className="form-label" htmlFor="oidc-scope-multiselect">
                      Scope
                    </label>
                    <OidcScopeSelect
                      id="oidc-scope-multiselect"
                      value={oidcForm.scope || []}
                      onChange={(scopes) => setOidcForm({ ...oidcForm, scope: scopes })}
                      disabled={saving}
                    />
                    <p className="form-hint oidc-scope-react-hint">
                      Search or pick scopes. <code>openid</code> is always included.
                    </p>
                  </div>
                  <div className="form-actions oidc-form-actions">
                    <button type="submit" className="btn-save" disabled={saving}>
                      {saving ? 'Saving…' : oidcClient ? 'Save changes' : 'Create client'}
                    </button>
                    {oidcClient && (
                      <button
                        type="button"
                        className="btn-oidc-delete"
                        onClick={deleteOidc}
                        disabled={saving}
                      >
                        <Trash2 size={16} aria-hidden />
                        Delete
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {tab === 'danger' && (
              <div className="tenant-config-card danger-card">
                <h3>
                  <Shield size={20} /> Danger zone
                </h3>
                <p>
                  Rotating the HMAC secret invalidates integrations that still use the old secret.
                  Coordinate with your team before rotating.
                </p>
                <button type="button" className="btn-danger-outline" onClick={regenerateHmac}>
                  Regenerate HMAC key
                </button>
                <hr />
                <p>
                  Deleting this tenant removes its configuration and cannot be undone. Type the tenant
                  ID when prompted to confirm.
                </p>
                <button type="button" className="btn-danger-solid" onClick={deleteTenant}>
                  Delete tenant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default TenantConfigurations;
